<template>
  <div class="container my-5">
    <div class="cards-grid">
      <div
        v-for="(cell, idx) in cells"
        :key="idx"
        class="grid-item"
      >
        <!-- Mittleres Feld (null) → zentrale Karte -->
        <div
          v-if="cell === null"
          class="card-center text-center p-4 m-4 glass-container"
        >
          <h4 class="center-card-title  m-0">Monitor Anwendungsbereiche</h4>
        </div>

        <!-- Alle anderen Karten (nur wenn cell wirklich ein Objekt ist) -->
        <div
          v-else-if="cell"
          class="card usage-card position-relative d-flex flex-column"
        >
          <div class="card-body d-flex flex-column">
            <h5 class="card-title text-start">
              <i :class="cell.icon + ' fs-3 me-2'"></i><br>
              {{ cell.title }}
            </h5>
            <ul class="mb-0 text-start flex-grow-1">
              <li
                v-for="(item, i) in cell.items"
                :key="i"
                :class="{ 'mb-2': i < cell.items.length - 1 }"
              >
                <i class="bi bi-check-circle me-2"></i>{{ item }}
              </li>
            </ul>
          </div>
        </div>

        <!-- Fallback für unerwartetes undefined -->
        <div v-else class="empty-cell"></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const cards = [
  {
    title: 'Einfaches Homeoffice',
    icon: 'bi bi-laptop',
    items: [
      'Größe als Faktor: Mittelgroße und Ultrawide Monitore können Produktivität erhöhen',
      'Daraus resultiert direkt die PPI: Schärfe und Auflösung ist entscheidend',
      'Ergonomie: Höhenverstellbarkeit und Neigungswinkel für rückenschonendes Arbeiten',
      'Design: Schmale Ränder helfen bei Multi-Monitor-Setups'
    ]
  },
  {
    title: 'Kompetitiv & manchmal Story',
    icon: 'bi bi-controller',
    items: [
      'Schnell & flüssig: 144–165 Hz für kompetitive Titel',
      'Detailreich: WQHD als Balance zwischen Schärfe und Performance',
      'Gute Farben: IPS oder noch besser OLED für lebendige Welten und klare Sicht',
      'Reaktionsschnell: ≤ 1–4 ms für präzises Gameplay',
      'Adaptiv synchron: FreeSync oder G-Sync',
      'Größe mit Übersicht: 27 Zoll als Sweet Spot'
    ]
  },
  {
    title: 'Gaming: Story und Immersion',
    icon: 'bi bi-display',
    items: [
      'Hohe Auflösung (WQHD oder 4K) für mehr Ansehnlichkeit',
      'Detailgetreu: Starke Farb- und Kontrastdarstellung, ideal mit HDR',
      'Bessere Immersion: Große Bildschirmdiagonale, Curved oder Ultrawide',
      'Schmale Ränder für Multi-Monitor-Setups',
      'Panelart: VA/IPS oder OLED je nach Budget'
    ]
  },
  {
    title: 'Gaming: Bis zum E-Sports',
    icon: 'bi bi-trophy',
    items: [
      'Maximal flüssig: 240–300 Hz',
      'Keine Verzögerung: Sehr niedrige Reaktionszeit',
      'G-Sync/FreeSync gegen Tearing',
      'Alles im Blick: 24–27 Zoll'
    ]
  },
  {
    title: 'Gaming: Allround',
    icon: 'bi bi-speedometer',
    items: [
      'Gute Allround-Specs: 75–144 Hz, WQHD',
      'Solide Farbdarstellung (IPS/VA)',
      'Reaktionszeit ~1–5 ms',
      'FreeSync oder G-Sync kompatibel'
    ]
  },
  {
    title: 'AAA-Singleplayer-Enthusiast',
    icon: 'bi bi-film',
    items: [
      'Eindrucksvoll scharf: WQHD oder 4K UHD',
      'Geschmeidig: 100–144 Hz (WQHD) bzw. 60–144 Hz (4K)',
      'Intensive Farben: IPS oder OLED',
      'Mehr Farbtiefe: 10-Bit ohne FRC',
      'Eintauchen: 27–38 Zoll, gerne Curved'
    ]
  },
  {
    title: 'Hybrid: Gaming & Office',
    icon: 'bi bi-tools',
    items: [
      'Vielseitig scharf: WQHD',
      'Angenehm flüssig: 100–144 Hz',
      'IPS-Panel für präzise Farben',
      'Oder VA für besseres Schwarz',
      'USB-C oder integrierter KVM-Switch'
    ]
  },
  {
    title: 'Produktiv: Für Entwickler & Programmierer',
    icon: 'bi bi-code-slash',
    items: [
      'Klarer Code: WQHD oder höher',
      'Mehr Übersicht: 27–34 Zoll',
      'Ergonomie: Höhenverstellbar mit Pivot',
      'Augenfreundlich: Flimmerfrei',
      'Flüssiges Scrollen: 60–100 Hz'
    ]
  }
]

// 3×3-Matrix mit null in der Mitte
const grid = computed(() => [
  [ cards[0], cards[1], cards[2] ],
  [ cards[3], null,      cards[4] ],
  [ cards[5], cards[6], cards[7] ]
])

const cells = computed(() => grid.value.flat())
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap');
@import 'https://cdn.jsdelivr.net/npm/bootstrap-icons@1.13.1/font/bootstrap-icons.css';

/* Default: Desktop ab 992px → 3 Spalten */
.cards-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(3, 1fr);
  align-items: stretch;
}

/* Tablet: 576px bis 991.98px → 2 Spalten */
@media (min-width: 576px) and (max-width: 991.98px) {
  .cards-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* Smartphone: bis 575.98px → 1 Spalte, zentrale Karte ausblenden */
@media (max-width: 575.98px) {
  .cards-grid {
    grid-template-columns: 1fr;
    display: block;
  }
  .card-center {
    display: none !important;
  }
}

/* Gemeinsame Styles für alle Viewports */
.grid-item {
  display: flex;
}
.grid-item > .usage-card,
.grid-item > .card-center {
  display: flex;
  flex-direction: column;
  width: 100%;
}

/* 1) Karte: nur noch Gradient, kein Glass mehr */
.card-center {
  /* entferne Glass-Stile */
  background: linear-gradient(211deg, #60a5fa 0%, #0c53c6de 100%);
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  border: none;
  box-shadow: none;

  /* behalte deine bisherigen Dimensionen */
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  padding: 1rem;  /* oder dein p-4 */
  margin: 1rem;   /* oder dein m-4 */
  position: relative;
}

/* 2) Glass-Overlay jetzt nur auf den Titel anwenden */
.center-card-title {
  position: relative;
  display: inline-block;       /* damit das Blur nur diesen Bereich betrifft */
  background: rgba(255,255,255,0.15);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 8px;
  padding: 0.5rem 1rem;
  transition: all 0.3s ease;
  /* Textfarbe optional dunkler wählen für besseren Kontrast:
     color: #111; */
}

/* 3) Optional: Hover-Effekt auf dem Text */
.center-card-title:hover {
  background: rgba(255,255,255,0.25);
  border-color: rgba(255,255,255,0.3);
  transform: translateY(-2px);
}

.usage-card {
  background-color: #F9FAFB;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease-in-out;
}
.usage-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.12);
}

.card-body {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.card-title {
  font-size: 1.125rem;
  font-weight: 500;
  color: #3d84f6;
  margin-bottom: 1rem;
}

ul {
  list-style: none;
  padding-left: 0;
}
li {
  font-size: 0.875rem;
  color: #4B5563;
  line-height: 1.4;
}
.empty-cell {
  flex: 1;
}


</style>


